import { useToast } from "@/hooks/use-toast";
import { algod, defaultValidityWindow } from "@/lib/algo";
import { verifySignedTransactions } from "@/lib/clients";
import { shorten, sleep } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { useWallet } from "@txnlab/use-wallet";
import algosdk from "algosdk";
import React, { useState, useEffect } from "react";
import { StepLabel } from "./step-label";
import { AlertDialogHeader, AlertDialogFooter } from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { encode } from "algo-msgpack-with-bigint";
import { CopyButton } from "../copy-button";
import { ErrorComponent } from "../error-component";
import { StepTitle } from "./step-title";
import { SearchCode } from "lucide-react";
import config from "@/config";

export function transactionInner(
  type: string,
  firstTitle: React.ReactNode,
  children: React.ReactNode
): (
  step: number,
  encodedTxns: string,
  error: string,
  txId: string
) => React.ReactNode {
  return (step: number, encodedTxns: string, error: string, txId: string) => (
    <>
      <div className={`flex flex-col grow gap-1 ${txId ? "justify-between" : ""}`}>
        <div className="mb-4 text-primary flex items-center gap-1">
          <StepTitle title={type} error={error} step={step}>
            {firstTitle}
          </StepTitle>
        </div>
        {error ? (
          <ErrorComponent error={error} diagnostics={encodedTxns} />
        ) : (
          <div className="flex flex-col border-l-2 border-solid pl-2 border-muted-foreground gap-1">
            {txId ? (
              <>
                <div className="text-sm mb-1">Transaction ID: {shorten(txId)}</div>
                <div className="flex gap-1">
                  <CopyButton value={txId} className="text-muted-foreground" />
                  {step === 5 ? (
                    <Button
                      onClick={() => window.open(`${config.explorer}/${txId}`)}
                      size="sm"
                      variant="secondary"
                      className="text-muted-foreground"
                    >
                      <SearchCode />
                      EXPLORE
                    </Button>
                  ) : null}
                </div>
              </>
            ) : (
              children
            )}
          </div>
        )}
      </div>
    </>
  );
}

type OnClick = (event: any) => void;

interface TransactionDialogProps {
  type: string;
  title: string | React.ReactNode;
  trigger: (o: OnClick) => React.ReactNode;
  children: (
    step: number,
    encodedTxns: string,
    error: string,
    txId: string
  ) => React.ReactNode;
  transactionBuilder: () => Promise<algosdk.Transaction[]>;
  onSuccess: (logs?: Uint8Array[]) => void;
  onClose?: () => any;
}

export function TransactionDialog({
  type,
  title,
  trigger,
  children,
  transactionBuilder,
  onSuccess,
  onClose,
}: TransactionDialogProps) {
  const { signTransactions } = useWallet();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [encodedTxns, setEncodedTxns] = useState("");
  const [errorStep, setErrorStep] = useState(0);
  const [ac, setAc] = useState(new AbortController());
  const [error, setError] = useState<string>("");
  const [txId, setTxId] = useState<string>("");
  const [groupID, setGroupID] = useState("");
  const [logs, setLogs] = useState<Uint8Array[]>()
  const { toast } = useToast();

  const reset = () => {
    setStep(1);
    setErrorStep(0);
    setError("");
    setTxId("");
    setEncodedTxns("");
    setGroupID("");
    setAc(new AbortController());
    setLogs([])
  };

  useEffect(() => {
    if (open === false) {
      reset();
      if (step !== 1 && step !== 5 && onClose) {
        onClose();
      }
      if (step >= 4 && onSuccess) {
        onSuccess(logs);
      }
    }
  }, [step, open, onClose, onSuccess]);

  useEffect(() => {
    if (step === 1) {
    }
  }, [step]);

  const sign = async () => {
    let step = 1;
    let _ac = ac;
    try {
      setStep((step = 2));
      const txns = await transactionBuilder();
      if (txns[0].group)
        setGroupID(Buffer.from(txns[0].group!).toString("base64"));
      setEncodedTxns(
        Buffer.from(encode(txns.map((t) => t.get_obj_for_encoding()))).toString(
          "base64"
        )
      );
      const signed = await signTransactions(
        txns.map((t) => algosdk.encodeUnsignedTransaction(t))
      );
      if (_ac.signal.aborted) {
        toast({
          title: "Received cancelled transaction ",
          description: `Ignored received ${type} transaction from wallet because the action was cancelled.`,
        });
        return;
      }
      verifySignedTransactions(txns, signed);

      setStep((step = 3));

      await algod.sendRawTransaction(signed).do();

      const appCalls = txns.filter(({ type }) => type === "appl")
      const txId = appCalls[appCalls.length - 1].txID();

      setTxId(txId);

      setStep((step = 4));

      let waitTries = 3;
      while(waitTries) {
        try {
          const result = await algosdk.waitForConfirmation(algod, txId, defaultValidityWindow);
          if (result.logs)
            setLogs(result.logs)
          break;
        } catch(e) {
          if ((e as Error).message === "Failed to fetch") {
            await sleep(1000);
            waitTries--
          } else {
            throw e
          }
        }
      }

      setStep((step = 5));
    } catch (e) {
      console.error((e as Error).message);
      if (_ac.signal.aborted) {
        return;
      }
      setErrorStep(step);
      setError((e as Error).message);
    }
  };

  return (
    <AlertDialog open={open}>
      <AlertDialogTrigger asChild>
        {trigger(() => setOpen(true))}
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-[95vw] md:max-w-md md:min-w-[550px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="mb-2 text-center text-foreground/80">{title}</AlertDialogTitle>
        </AlertDialogHeader>

        <div className="flex justify-between gap-10">
          <div className="flex flex-grow flex-col text-muted-foreground">
            {children(step, encodedTxns, error, txId)}
          </div>
          <div className="hidden md:flex min-w-24 flex-col gap-2 text-muted-foreground">
            <StepLabel step={1} currentStep={step} errorStep={errorStep}>
              Review
            </StepLabel>
            <StepLabel step={2} currentStep={step} errorStep={errorStep}>
              Sign
            </StepLabel>
            <StepLabel step={3} currentStep={step} errorStep={errorStep}>
              Execute
            </StepLabel>
            <StepLabel step={4} currentStep={step} errorStep={errorStep}>
              Confirm
            </StepLabel>
          </div>
        </div>

        {groupID && step === 2 ? (
          <div className="relative">
            <div className="text-sm truncate max-w-[80vw] mt-3">
              <span className="text-muted-foreground mr-1">Group ID</span>{" "}
              {groupID}
            </div>
          </div>
        ) : null}

        <AlertDialogFooter className="mt-2">
          {error || (step != 3 && step != 4) ? (
            <AlertDialogCancel
              onClick={() => {
                ac.abort();
                setOpen(false);
              }}
            >
              {step === 5 || error ? <>Close</> : <>Cancel</>}
            </AlertDialogCancel>
          ) : null}
          {step === 1 ? (
            <AlertDialogAction onClick={sign}>Sign</AlertDialogAction>
          ) : null}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
